import React from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, notification, Typography, Space, List, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import request from '../../lib/request';
import { useCompanyId, CompanySelector } from '../../lib/books';
import ChecklistTemplateModal from '../../components/ChecklistTemplateModal';

/**
 * Service -> Sub Service -> Activity -> Service Task, rendered as nested
 * expandable lists (same 4-level catalog hierarchy ported from the ERPNext
 * build). Each level's "Add" button opens a small modal scoped to its
 * parent - mnemonics stay a flat 3-char field per level, matching the API.
 */
export default function ServicesPage() {
  const { companyId, companies, isSystemAdmin } = useCompanyId();
  // A System Administrator maintains the ADMIN template (0) or one company's own catalogue.
  const [catalogueCompanyId, setCatalogueCompanyId] = React.useState(isSystemAdmin ? 0 : companyId);
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modal, setModal] = React.useState(null); // { level: 'service'|'sub-service'|'activity'|'task', parentId }
  const [form] = Form.useForm();
  const [checklistTask, setChecklistTask] = React.useState(null);

  const fetchServices = () => {
    setLoading(true);
    request
      .get('/services', catalogueCompanyId ? { company_id: catalogueCompanyId } : undefined)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setServices(r.data);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchServices, [catalogueCompanyId]);

  const openModal = (level, parentId) => {
    form.resetFields();
    setModal({ level, parentId });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const endpoints = {
        service: ['/services', { ...values, company_id: catalogueCompanyId || null }],
        'sub-service': ['/services/sub-services', { ...values, service_id: modal.parentId }],
        activity: ['/services/activities', { ...values, sub_service_id: modal.parentId }],
        task: ['/services/tasks', { ...values, activity_id: modal.parentId }],
      };
      const [path, body] = endpoints[modal.level];

      request
        .post(path, body)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Saved' });
            setModal(null);
            fetchServices();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const columns = [
    { title: 'Mnemonic', dataIndex: 'mnemonic', width: 100, render: (v) => <Tag>{v}</Tag> },
    { title: 'Service', dataIndex: 'service_name' },
    { title: 'Description', dataIndex: 'description' },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button size="small" icon={<PlusOutlined />} onClick={() => openModal('sub-service', record.id)}>
          Add Sub Service
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Services
          {isSystemAdmin && (
            <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal', marginLeft: 12 }}>
              {catalogueCompanyId ? 'company catalogue' : 'ADMIN template - copied into every new company'}
            </Typography.Text>
          )}
        </Typography.Title>
        <Space>
          <CompanySelector
            style={{ width: 240 }}
            value={catalogueCompanyId}
            onChange={setCatalogueCompanyId}
            options={[{ value: 0, label: 'ADMIN template' }, ...companies.map((c) => ({ value: c.id, label: c.name }))]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('service', null)}>
            New Service
          </Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={services}
        loading={loading}
        expandable={{
          expandedRowRender: (service) => (
            <List
              size="small"
              header={<strong>Sub Services</strong>}
              dataSource={service.SubServices || []}
              renderItem={(sub) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span>
                        <Tag>{sub.mnemonic}</Tag> {sub.sub_service_name}
                      </span>
                      <Button size="small" onClick={() => openModal('activity', sub.id)}>
                        Add Activity
                      </Button>
                    </Space>
                    <List
                      size="small"
                      style={{ marginLeft: 24, marginTop: 8 }}
                      dataSource={sub.Activities || []}
                      renderItem={(activity) => (
                        <List.Item>
                          <div style={{ width: '100%' }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                              <span>
                                <Tag>{activity.mnemonic}</Tag> {activity.activity_name}
                              </span>
                              <Button size="small" onClick={() => openModal('task', activity.id)}>
                                Add Task
                              </Button>
                            </Space>
                            <List
                              size="small"
                              style={{ marginLeft: 24, marginTop: 8 }}
                              dataSource={(activity.ServiceTasks || []).sort((a, b) => (a.order || 0) - (b.order || 0))}
                              renderItem={(task) => (
                                <List.Item actions={[<Button key="cl" size="small" onClick={() => setChecklistTask(task)}>Checklist</Button>]}>
                                  <span>
                                    <Tag>{task.mnemonic}</Tag> {task.task_name}
                                    {task.order != null && <Tag color="blue">order {task.order}</Tag>}
                                    {task.default_days != null && <Tag color="green">{task.default_days}d</Tag>}
                                  </span>
                                </List.Item>
                              )}
                            />
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>
                </List.Item>
              )}
            />
          ),
        }}
      />

      <ChecklistTemplateModal task={checklistTask} onClose={() => setChecklistTask(null)} />

      <Modal
        title={
          {
            service: 'New Service',
            'sub-service': 'New Sub Service',
            activity: 'New Activity',
            task: 'New Service Task',
          }[modal?.level]
        }
        open={!!modal}
        onCancel={() => setModal(null)}
        onOk={handleSave}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="mnemonic" label="Mnemonic (3 characters)" rules={[{ required: true, len: 3 }]}>
            <Input maxLength={3} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          {modal?.level === 'service' && (
            <Form.Item name="service_name" label="Service Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {modal?.level === 'sub-service' && (
            <Form.Item name="sub_service_name" label="Sub Service Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {modal?.level === 'activity' && (
            <Form.Item name="activity_name" label="Activity Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          {modal?.level === 'task' && (
            <>
              <Form.Item name="task_name" label="Task Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="order" label="Order">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="default_days" label="Default Days">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
